import * as T from 'three';
import {RoundedBoxGeometry} from 'three/addons/geometries/RoundedBoxGeometry.js';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';

function weaveNormal(){
  const size=32,pixels=new Uint8Array(size*size*4);
  for(let y=0;y<size;y++)for(let x=0;x<size;x++){
    const i=(y*size+x)*4;
    pixels[i]=128+Math.round(25*Math.sin(x*Math.PI/2));
    pixels[i+1]=128+Math.round(25*Math.sin(y*Math.PI/2));pixels[i+2]=252;pixels[i+3]=255;
  }
  const texture=new T.DataTexture(pixels,size,size);
  texture.wrapS=texture.wrapT=T.RepeatWrapping;texture.repeat.set(8,6);texture.needsUpdate=true;
  return texture;
}

function rimGeometry(width,depth,y,radius,tube){
  const x=width/2,z=depth/2,r=radius,path=new T.CurvePath();
  const point=(a,b)=>new T.Vector3(a,y,b);
  const line=(a,b)=>path.add(new T.LineCurve3(point(...a),point(...b)));
  const corner=(a,b,c)=>path.add(new T.QuadraticBezierCurve3(point(...a),point(...b),point(...c)));
  line([-x+r,-z],[x-r,-z]);corner([x-r,-z],[x,-z],[x,-z+r]);
  line([x,-z+r],[x,z-r]);corner([x,z-r],[x,z],[x-r,z]);
  line([x-r,z],[-x+r,z]);corner([-x+r,z],[-x,z],[-x,z-r]);
  line([-x,z-r],[-x,-z+r]);corner([-x,-z+r],[-x,-z],[-x+r,-z]);
  return new T.TubeGeometry(path,128,tube,10,true);
}

export function makeToyBox(){
  const group=new T.Group();group.name='Open toy box';
  const normalMap=weaveNormal();
  const exterior=new T.MeshStandardMaterial({name:'warm canvas',color:'#e8dfcb',roughness:.87,normalMap,normalScale:new T.Vector2(.15,.15)});
  const interior=new T.MeshStandardMaterial({name:'blue lining',color:'#66818b',roughness:.91,normalMap,normalScale:new T.Vector2(.12,.12)});
  const wood=new T.MeshStandardMaterial({name:'rounded birch lip',color:'#c8a577',roughness:.57});
  const recess=new T.MeshStandardMaterial({name:'handle recess',color:'#4b5b5e',roughness:.94});
  const thread=new T.MeshStandardMaterial({name:'canvas stitching',color:'#faf2df',roughness:1});
  const batches=new Map(),support=[];
  function add(geometry,material,x=0,y=0,z=0){
    geometry.translate(x,y,z);
    const flat=geometry.index?geometry.toNonIndexed():geometry;
    if(flat!==geometry)geometry.dispose();
    if(!batches.has(material))batches.set(material,[]);
    batches.get(material).push(flat);
    const p=flat.attributes.position;
    for(let i=0;i<p.count;i++)support.push(p.getX(i),p.getY(i),p.getZ(i));
  }
  function panel(w,h,d,x,y,z,material,r=.04){
    add(new RoundedBoxGeometry(w,h,d,3,Math.min(r,w/2,h/2,d/2)),material,x,y,z);
  }
  // Five exterior panels leave the entire top open. The lining is inset on
  // each wall and floor rather than a second solid cuboid inside the bin.
  panel(1.96,.12,1.36,0,-.58,0,exterior,.055);
  for(const side of [-1,1]){
    panel(.12,1.18,1.38,side*.94,.01,0,exterior,.055);
    panel(1.80,1.18,.12,0,.01,side*.64,exterior,.055);
    panel(.022,1.09,1.17,side*.873,.015,0,interior,.011);
    panel(1.73,1.09,.022,0,.015,side*.573,interior,.011);
    // Dark, rounded handle insets avoid costly Boolean wall cutouts.
    panel(.48,.102,.012,0,.285,side*.704,recess,.044);
  }
  panel(1.76,.027,1.17,0,-.509,0,interior,.013);
  add(rimGeometry(1.89,1.29,.601,.105,.054),wood);
  // Two low wooden runners and restrained seams make the construction legible.
  for(const side of [-1,1])panel(1.66,.038,.11,0,-.653,side*.43,wood,.018);
  for(const side of [-1,1]){
    for(let x=-.78;x<=.79;x+=.075)add(new T.BoxGeometry(.025,.004,.004),thread,x,.46,side*.702);
    for(const x of [-.82,.82])for(let y=-.42;y<.40;y+=.072)add(new T.BoxGeometry(.004,.025,.004),thread,x,y,side*.702);
  }
  for(const [material,geometries] of batches){
    const combined=mergeGeometries(geometries),mesh=new T.Mesh(combined,material);
    mesh.name=material.name;mesh.castShadow=true;mesh.receiveShadow=true;group.add(mesh);
    for(const geometry of geometries)geometry.dispose();
  }
  const coordinates=new Float32Array(support),rotation=new T.Matrix4();
  const upright=new T.Quaternion(),tipped=new T.Quaternion().setFromUnitVectors(new T.Vector3(0,1,0),new T.Vector3(.80,.15,.58).normalize());
  function setPourProgress(value){
    if(!Number.isFinite(value))return;
    const t=T.MathUtils.clamp(value,0,1),smooth=t*t*(3-2*t);
    group.quaternion.slerpQuaternions(upright,tipped,smooth);
    rotation.makeRotationFromQuaternion(group.quaternion);
    const e=rotation.elements;let floor=Infinity;
    for(let i=0;i<coordinates.length;i+=3)floor=Math.min(floor,e[1]*coordinates[i]+e[5]*coordinates[i+1]+e[9]*coordinates[i+2]);
    group.position.set(-1.65,-floor,-.9);group.updateMatrixWorld(true);
  }
  function getMouth(out=new T.Vector3()){
    group.updateWorldMatrix(true,false);
    return out.set(0,.62,0).applyMatrix4(group.matrixWorld);
  }
  let opacity=1;
  function setOpacity(value){
    opacity=T.MathUtils.clamp(value,0,1);group.visible=opacity>0;
    for(const mesh of group.children){
      const transparent=opacity<1,material=mesh.material;
      if(material.transparent!==transparent){material.transparent=transparent;material.needsUpdate=true;}
      material.opacity=opacity;material.depthWrite=!transparent;
    }
  }
  setPourProgress(0);
  return {group,setPourProgress,getMouth,setOpacity,get opacity(){return opacity;}};
}
